import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface AccessibilitySettings {
  highContrast: boolean;
  reducedMotion: boolean;
  screenReaderMode: boolean;
  keyboardNavigation: boolean;
  fontSize: 'small' | 'medium' | 'large' | 'extra-large';
  focusIndicators: 'default' | 'enhanced' | 'high-visibility';
}

export interface AccessibilityAudit {
  componentId: string;
  issues: AccessibilityIssue[];
  score: number; // 0-100
  lastChecked: Date;
}

export interface AccessibilityIssue {
  id: string;
  severity: 'error' | 'warning' | 'info';
  rule: string;
  description: string;
  element: string;
  suggestion: string;
  wcagLevel: 'A' | 'AA' | 'AAA';
  wcagCriterion: string;
}

@Injectable({
  providedIn: 'root',
})
export class AccessibilityService {
  private settingsSubject = new BehaviorSubject<AccessibilitySettings>(this.getDefaultSettings());
  private auditResultsSubject = new BehaviorSubject<AccessibilityAudit[]>([]);
  
  settings$ = this.settingsSubject.asObservable();
  auditResults$ = this.auditResultsSubject.asObservable();

  constructor() {
    this.loadSettings();
    this.detectSystemPreferences();
    this.setupKeyboardNavigation();
  }

  // Gerenciamento de Configurações
  updateSettings(settings: Partial<AccessibilitySettings>) {
    const currentSettings = this.settingsSubject.value;
    const newSettings = { ...currentSettings, ...settings };
    
    this.settingsSubject.next(newSettings);
    this.saveSettings(newSettings);
    this.applySettings(newSettings);
  }

  getSettings(): AccessibilitySettings {
    return this.settingsSubject.value;
  }

  // Auditoria de Acessibilidade
  auditComponent(componentElement: HTMLElement, componentId: string): AccessibilityAudit {
    const issues: AccessibilityIssue[] = [];
    
    // Verifica labels ausentes
    issues.push(...this.checkLabels(componentElement));
    
    // Verifica contraste de cor
    issues.push(...this.checkColorContrast(componentElement));
    
    // Verifica acessibilidade de teclado
    issues.push(...this.checkKeyboardAccess(componentElement));
    
    // Verifica atributos ARIA
    issues.push(...this.checkAriaAttributes(componentElement));
    
    // Verifica gerenciamento de foco
    issues.push(...this.checkFocusManagement(componentElement));
    
    // Calcula pontuação
    const score = this.calculateAccessibilityScore(issues);
    
    const audit: AccessibilityAudit = {
      componentId,
      issues,
      score,
      lastChecked: new Date(),
    };
    
    this.updateAuditResults(audit);
    return audit;
  }

  // Navegação por Teclado
  setupKeyboardNavigation() {
    document.addEventListener('keydown', this.handleKeyboardNavigation.bind(this));
  }

  private handleKeyboardNavigation(event: KeyboardEvent) {
    const settings = this.getSettings();
    if (!settings.keyboardNavigation) return;

    switch (event.key) {
      case 'Tab':
        this.enhanceFocusVisibility(event);
        break;
      case 'Escape':
        this.handleEscapeKey(event);
        break;
      case 'Enter':
      case ' ':
        this.handleActivationKeys(event);
        break;
      case 'ArrowUp':
      case 'ArrowDown':
      case 'ArrowLeft':
      case 'ArrowRight':
        this.handleArrowKeys(event);
        break;
    }
  }

  // Gerenciamento de Foco
  setFocus(element: HTMLElement, options?: { preventScroll?: boolean }) {
    if (!element) return;
    
    element.focus(options);
    this.announceFocusChange(element);
  }

  trapFocus(container: HTMLElement) {
    const focusableElements = this.getFocusableElements(container);
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    container.addEventListener('keydown', handleTabKey);
    
    // Retorna função de limpeza
    return () => {
      container.removeEventListener('keydown', handleTabKey);
    };
  }

  // Suporte ao Leitor de Tela
  announceToScreenReader(message: string, priority: 'polite' | 'assertive' = 'polite') {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', priority);
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    
    // Remove após anúncio
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }

  // Utilitários de Contraste de Cor
  checkColorContrast(element: HTMLElement): AccessibilityIssue[] {
    const issues: AccessibilityIssue[] = [];
    const styles = window.getComputedStyle(element);
    const backgroundColor = styles.backgroundColor;
    const color = styles.color;
    
    if (backgroundColor && color) {
      const contrast = this.calculateContrast(backgroundColor, color);
      
      if (contrast < 4.5) {
        issues.push({
          id: `contrast-${Date.now()}`,
          severity: contrast < 3 ? 'error' : 'warning',
          rule: 'color-contrast',
          description: `Color contrast ratio is ${contrast.toFixed(2)}:1, which is below the WCAG AA standard of 4.5:1`,
          element: element.tagName.toLowerCase(),
          suggestion: 'Increase the contrast between text and background colors',
          wcagLevel: 'AA',
          wcagCriterion: '1.4.3 Contrast (Minimum)',
        });
      }
    }
    
    return issues;
  }

  // Métodos de Auditoria
  private checkLabels(element: HTMLElement): AccessibilityIssue[] {
    const issues: AccessibilityIssue[] = [];
    const inputs = element.querySelectorAll('input, select, textarea');
    
    inputs.forEach((input) => {
      const hasLabel = this.hasAccessibleLabel(input as HTMLElement);
      if (!hasLabel) {
        issues.push({
          id: `label-${Date.now()}-${Math.random()}`,
          severity: 'error',
          rule: 'form-label',
          description: 'Form control is missing an accessible label',
          element: input.tagName.toLowerCase(),
          suggestion: 'Add a label element or aria-label attribute',
          wcagLevel: 'A',
          wcagCriterion: '1.3.1 Info and Relationships',
        });
      }
    });
    
    return issues;
  }

  private checkKeyboardAccess(element: HTMLElement): AccessibilityIssue[] {
    const issues: AccessibilityIssue[] = [];
    const interactiveElements = element.querySelectorAll('button, a, input, select, textarea, [tabindex]');
    
    interactiveElements.forEach((el) => {
      const htmlElement = el as HTMLElement;
      if (!this.isKeyboardAccessible(htmlElement)) {
        issues.push({
          id: `keyboard-${Date.now()}-${Math.random()}`,
          severity: 'error',
          rule: 'keyboard-accessible',
          description: 'Interactive element is not keyboard accessible',
          element: el.tagName.toLowerCase(),
          suggestion: 'Ensure element can be focused and activated with keyboard',
          wcagLevel: 'A',
          wcagCriterion: '2.1.1 Keyboard',
        });
      }
    });
    
    return issues;
  }

  private checkAriaAttributes(element: HTMLElement): AccessibilityIssue[] {
    const issues: AccessibilityIssue[] = [];
    
    // Verifica atributos ARIA inválidos
    const ariaAttributes = Array.from(element.attributes).filter(attr => 
      attr.name.startsWith('aria-')
    );
    
    ariaAttributes.forEach(attr => {
      if (!this.isValidAriaAttribute(attr.name, attr.value)) {
        issues.push({
          id: `aria-${Date.now()}-${Math.random()}`,
          severity: 'warning',
          rule: 'valid-aria',
          description: `Invalid ARIA attribute: ${attr.name}="${attr.value}"`,
          element: element.tagName.toLowerCase(),
          suggestion: 'Use valid ARIA attributes and values',
          wcagLevel: 'A',
          wcagCriterion: '4.1.2 Name, Role, Value',
        });
      }
    });
    
    return issues;
  }

  private checkFocusManagement(element: HTMLElement): AccessibilityIssue[] {
    const issues: AccessibilityIssue[] = [];
    
    // Verifica armadilhas de foco em modais
    if (element.getAttribute('role') === 'dialog' || element.classList.contains('modal')) {
      const focusableElements = this.getFocusableElements(element);
      if (focusableElements.length === 0) {
        issues.push({
          id: `focus-trap-${Date.now()}`,
          severity: 'error',
          rule: 'focus-management',
          description: 'Modal or dialog has no focusable elements',
          element: element.tagName.toLowerCase(),
          suggestion: 'Ensure modals have at least one focusable element',
          wcagLevel: 'AA',
          wcagCriterion: '2.4.3 Focus Order',
        });
      }
    }
    
    return issues;
  }

  // Métodos Auxiliares
  private getDefaultSettings(): AccessibilitySettings {
    return {
      highContrast: false,
      reducedMotion: false,
      screenReaderMode: false,
      keyboardNavigation: true,
      fontSize: 'medium',
      focusIndicators: 'default',
    };
  }

  private detectSystemPreferences() {
    const settings = this.getSettings();
    
    // Detecta preferência de alto contraste
    if (window.matchMedia('(prefers-contrast: high)').matches) {
      settings.highContrast = true;
    }
    
    // Detecta preferência de movimento reduzido
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      settings.reducedMotion = true;
    }
    
    this.updateSettings(settings);
  }

  private applySettings(settings: AccessibilitySettings) {
    const root = document.documentElement;
    
    // Aplica alto contraste
    root.classList.toggle('high-contrast', settings.highContrast);
    
    // Aplica movimento reduzido
    root.classList.toggle('reduced-motion', settings.reducedMotion);
    
    // Aplica tamanho da fonte
    root.setAttribute('data-font-size', settings.fontSize);
    
    // Aplica indicadores de foco
    root.setAttribute('data-focus-style', settings.focusIndicators);
  }

  private loadSettings() {
    const saved = localStorage.getItem('accessibility-settings');
    if (saved) {
      try {
        const settings = JSON.parse(saved);
        this.settingsSubject.next({ ...this.getDefaultSettings(), ...settings });
      } catch {
        // Usa padrões
      }
    }
  }

  private saveSettings(settings: AccessibilitySettings) {
    localStorage.setItem('accessibility-settings', JSON.stringify(settings));
  }

  private calculateAccessibilityScore(issues: AccessibilityIssue[]): number {
    if (issues.length === 0) return 100;
    
    const errorWeight = 10;
    const warningWeight = 5;
    const infoWeight = 1;
    
    const totalDeductions = issues.reduce((sum, issue) => {
      switch (issue.severity) {
        case 'error': return sum + errorWeight;
        case 'warning': return sum + warningWeight;
        case 'info': return sum + infoWeight;
        default: return sum;
      }
    }, 0);
    
    return Math.max(0, 100 - totalDeductions);
  }

  private updateAuditResults(audit: AccessibilityAudit) {
    const currentResults = this.auditResultsSubject.value;
    const existingIndex = currentResults.findIndex(r => r.componentId === audit.componentId);
    
    if (existingIndex >= 0) {
      currentResults[existingIndex] = audit;
    } else {
      currentResults.push(audit);
    }
    
    this.auditResultsSubject.next([...currentResults]);
  }

  private hasAccessibleLabel(element: HTMLElement): boolean {
    return !!(
      element.getAttribute('aria-label') ||
      element.getAttribute('aria-labelledby') ||
      element.querySelector('label') ||
      element.closest('label')
    );
  }

  private isKeyboardAccessible(element: HTMLElement): boolean {
    const tabIndex = element.getAttribute('tabindex');
    return tabIndex !== '-1' && !element.hasAttribute('disabled');
  }

  private isValidAriaAttribute(name: string, value: string): boolean {
    // Validação simplificada - na implementação real, use especificação ARIA abrangente
    const validAttributes = [
      'aria-label', 'aria-labelledby', 'aria-describedby', 'aria-expanded',
      'aria-hidden', 'aria-live', 'aria-atomic', 'aria-relevant'
    ];
    return validAttributes.includes(name) && value.trim() !== '';
  }

  private getFocusableElements(container: HTMLElement): HTMLElement[] {
    const selector = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');
    
    return Array.from(container.querySelectorAll(selector)) as HTMLElement[];
  }

  private calculateContrast(bg: string, fg: string): number {
    // Cálculo de contraste simplificado - use biblioteca de cores adequada em produção
    return Math.random() * 10 + 1; // Implementação simulada
  }

  private enhanceFocusVisibility(event: KeyboardEvent) {
    // Adiciona indicadores de foco aprimorados ao navegar com tab
    const target = event.target as HTMLElement;
    if (target) {
      target.classList.add('keyboard-focused');
      setTimeout(() => target.classList.remove('keyboard-focused'), 3000);
    }
  }

  private handleEscapeKey(event: KeyboardEvent) {
    // Fecha modais, dropdowns, etc. com Escape
    const activeModal = document.querySelector('.ant-modal:not(.ant-modal-hidden)');
    if (activeModal) {
      const closeButton = activeModal.querySelector('.ant-modal-close') as HTMLElement;
      if (closeButton) {
        closeButton.click();
      }
    }
  }

  private handleActivationKeys(event: KeyboardEvent) {
    const target = event.target as HTMLElement;
    if (target.getAttribute('role') === 'button' && !target.matches('button, input, a')) {
      event.preventDefault();
      target.click();
    }
  }

  private handleArrowKeys(event: KeyboardEvent) {
    // Gerencia navegação com teclas de seta em listas, menus, etc.
    const target = event.target as HTMLElement;
    const container = target.closest('[role="listbox"], [role="menu"], [role="tablist"]');
    
    if (container) {
      event.preventDefault();
      this.navigateWithArrows(event, container as HTMLElement);
    }
  }

  private navigateWithArrows(event: KeyboardEvent, container: HTMLElement) {
    const items = this.getFocusableElements(container);
    const currentIndex = items.indexOf(event.target as HTMLElement);
    
    let nextIndex = currentIndex;
    
    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowLeft':
        nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
        break;
      case 'ArrowDown':
      case 'ArrowRight':
        nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
        break;
    }
    
    if (items[nextIndex]) {
      items[nextIndex].focus();
    }
  }

  private announceFocusChange(element: HTMLElement) {
    const settings = this.getSettings();
    if (!settings.screenReaderMode) return;
    
    const label = element.getAttribute('aria-label') || 
                  element.textContent || 
                  element.getAttribute('title') || 
                  'Interactive element';
    
    this.announceToScreenReader(`Focused on ${label}`, 'polite');
  }
}
